import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api, getSyncStatus } from "@/lib/api";
import { toast } from "sonner";
import { Database, AlertTriangle, Loader2, CalendarClock } from "lucide-react";

const DEFAULT_URL = "https://docs.google.com/spreadsheets/d/1w7kdIT6IawVZMjauNheGhLykzhz1fnyYDUMSJTFL7To/edit?usp=sharing";

const inputCls =
  "rounded-none border-[#0A1F44]/30 bg-transparent focus-visible:ring-1 focus-visible:ring-[#C0982E] focus-visible:border-[#C0982E] font-mono";

const fmtWhen = (iso) => {
  if (!iso) return null;
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
};

export const RosterImportDialog = ({ open, onOpenChange, onImported, year, linkedSheet }) => {
  const [url, setUrl] = useState(linkedSheet || DEFAULT_URL);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [syncInfo, setSyncInfo] = useState(null);

  useEffect(() => {
    if (!open) { setData(null); setError(""); setUrl(linkedSheet || DEFAULT_URL); }
    else { getSyncStatus().then(setSyncInfo).catch(() => {}); }
  }, [open, linkedSheet]);

  const doPreview = async () => {
    if (!url.trim()) { toast.error("Paste the Google Sheets link first"); return; }
    setLoading(true); setData(null); setError("");
    try {
      const { data: res } = await api.post("/import/neisd/preview", { url }, { params: { year } });
      setData(res);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not read that sheet.");
    } finally { setLoading(false); }
  };

  const doImport = async () => {
    setImporting(true);
    try {
      const { data: res } = await api.post("/import/neisd/commit", { url }, { params: { year } });
      const parts = [`${res.inserted} added`, `${res.updated} updated`];
      if (res.removed) parts.push(`${res.removed} removed`);
      toast.success(`${year}: ${parts.join(", ")}`);
      onImported();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally { setImporting(false); }
  };

  const depts = data ? Object.entries(data.departments).sort((a, b) => b[1] - a[1]) : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="roster-dialog" className="rounded-none border border-[#0A1F44] bg-[#F3F5F8] max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-[#0A1F44] flex items-center gap-2">
            <Database className="w-5 h-5 text-[#C0982E]" strokeWidth={1.5} /> Import Roster into {year}
          </DialogTitle>
          <DialogDescription className="text-[#0A1F44]/60">
            Paste the Google Sheet link for this year's roster — either the roster sheet itself or
            a dashboard that links to its Form-responses sheet. Sharing must be "Anyone with the
            link can view".
          </DialogDescription>
        </DialogHeader>

        <div className="py-2 space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-wider">Google Sheets Link</Label>
            <Input
              data-testid="roster-url-input"
              className={inputCls}
              value={url}
              onChange={(e) => { setUrl(e.target.value); setData(null); setError(""); }}
              placeholder="https://docs.google.com/spreadsheets/d/…?gid=…"
            />
            {linkedSheet ? (
              <div data-testid="roster-linked-sheet" className="mt-2 text-[11px] text-[#0A1F44]/55">
                Currently linked to <strong>{year}</strong>:{" "}
                <a href={linkedSheet} target="_blank" rel="noreferrer" className="text-[#C0982E] underline break-all">{linkedSheet}</a>
                <div className="mt-0.5 text-[#0A1F44]/45">Paste a different link above to change the sheet this year syncs from.</div>
              </div>
            ) : (
              <div data-testid="roster-no-linked-sheet" className="mt-2 text-[11px] text-[#0A1F44]/55">
                No sheet linked to <strong>{year}</strong> yet — importing will link this one.
              </div>
            )}
          </div>

          {syncInfo && (
            <div data-testid="auto-sync-note" className="flex items-start gap-2 border border-[#0A1F44]/15 bg-[#0A1F44]/[0.03] p-3 text-[11px] text-[#0A1F44]/70">
              <CalendarClock className="w-4 h-4 shrink-0 mt-0.5 text-[#C0982E]" />
              <div>
                <span className="font-semibold">Auto-sync:</span> {syncInfo.schedule}. The active year's linked sheet is re-imported automatically.
                {syncInfo.last_auto_sync?.at && (
                  <div className="mt-0.5 text-[#0A1F44]/45">
                    Last auto-sync: {fmtWhen(syncInfo.last_auto_sync.at)} ({syncInfo.last_auto_sync.year}, {syncInfo.last_auto_sync.updated} updated)
                  </div>
                )}
              </div>
            </div>
          )}

          {loading && (
            <div data-testid="roster-loading" className="flex items-center gap-2 text-[#0A1F44]/60 py-4">
              <Loader2 className="w-4 h-4 animate-spin" /> Reading the sheet…
            </div>
          )}

          {error && (
            <div data-testid="roster-error" className="flex gap-2 border border-[#b3402a]/40 bg-[#b3402a]/5 p-3 text-sm text-[#b3402a]">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /> <span>{error}</span>
            </div>
          )}

          {data && (
            <div data-testid="roster-summary" className="space-y-4">
              <div className="border border-[#0A1F44]/20 p-4">
                <div className="font-serif text-4xl text-[#0A1F44]">{data.count}</div>
                <div className="text-sm text-[#0A1F44]/60">staff members found</div>
              </div>
              <div data-testid="roster-changes" className="grid grid-cols-3 border border-[#0A1F44]/20">
                <div className="p-4 border-r border-[#0A1F44]/20">
                  <div className="font-serif text-3xl text-[#0A1F44]" data-testid="changes-added">+{data.added}</div>
                  <div className="text-[10px] uppercase tracking-[0.15em] text-[#0A1F44]/50">New</div>
                </div>
                <div className="p-4 border-r border-[#0A1F44]/20">
                  <div className="font-serif text-3xl text-[#0A1F44]" data-testid="changes-updated">{data.updated}</div>
                  <div className="text-[10px] uppercase tracking-[0.15em] text-[#0A1F44]/50">Updated</div>
                </div>
                <div className="p-4">
                  <div className={`font-serif text-3xl ${data.removed ? "text-[#b3402a]" : "text-[#0A1F44]/40"}`} data-testid="changes-removed">−{data.removed}</div>
                  <div className="text-[10px] uppercase tracking-[0.15em] text-[#0A1F44]/50">Removed</div>
                </div>
              </div>
              <div className="border border-[#0A1F44]/20">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#0A1F44]/50 px-4 py-2 bg-[#E6EAF1]">By Department</div>
                <div className="grid grid-cols-2 gap-x-4 p-4 text-sm">
                  {depts.map(([name, n]) => (
                    <div key={name} className="flex justify-between border-b border-[#0A1F44]/10 py-1">
                      <span>{name}</span><span className="text-[#C0982E] font-medium">{n}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex gap-2 border-l-4 border-[#C0982E] bg-[#C0982E]/10 p-3 text-sm text-[#0A1F44]">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-[#C0982E]" />
                Syncs into <strong className="mx-1">{year}</strong>: matches existing staff by
                email / staff ID.
                {data.removed > 0 && (
                  <span data-testid="removed-warning" className="text-[#b3402a] font-medium">
                    &nbsp;{data.removed} current member{data.removed === 1 ? "" : "s"} not in the sheet will be removed.
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button data-testid="roster-cancel" variant="outline" className="rounded-none border-[#0A1F44] text-[#0A1F44] hover:bg-[#E6EAF1]" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          {!data ? (
            <Button data-testid="roster-preview-btn" disabled={loading} className="rounded-none bg-[#0A1F44] text-[#F3F5F8] hover:bg-[#C0982E]" onClick={doPreview}>
              {loading ? "Reading…" : "Preview"}
            </Button>
          ) : (
            <Button data-testid="roster-confirm-btn" disabled={importing} className="rounded-none bg-[#0A1F44] text-[#F3F5F8] hover:bg-[#C0982E]" onClick={doImport}>
              {importing ? "Importing…" : `Sync ${data.count} into ${year}`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
